import createDebug from 'debug';
import { Globe, Loader2, ExternalLink, Settings as SettingsIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import type { QueueResponse } from '@/api/client';
import {
	getQueuePublicAccess,
	type PublicAccessStatus,
} from '@/api/public-access';
import { CopyUrlButton } from '@/components/CopyUrlButton';
import { PublicAccessBadge } from '@/components/PublicAccessBadge';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';

const debug = createDebug('app:public-access-manager');

interface PublicAccessManagerDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	queues: QueueResponse[];
	onQueueSettings?: (queue: QueueResponse) => void;
}

interface QueuePublicAccessState {
	queue: QueueResponse;
	status: PublicAccessStatus | null;
	loading: boolean;
}

export function PublicAccessManagerDialog({
	open,
	onOpenChange,
	queues,
	onQueueSettings,
}: PublicAccessManagerDialogProps) {
	const [queueStates, setQueueStates] = useState<Map<number, QueuePublicAccessState>>(new Map());
	const [loading, setLoading] = useState(false);

	// Load public access status for all queues
	const loadAllStatuses = useCallback(async () => {
		if (!open) return;

		setLoading(true);
		const states = new Map<number, QueuePublicAccessState>();

		// Filter out incoming queue (id=0)
		const userQueues = queues.filter((q) => q.id !== 0);

		await Promise.all(
			userQueues.map(async (queue) => {
				try {
					const status = await getQueuePublicAccess(queue.id);
					states.set(queue.id, { queue, status, loading: false });
				} catch (err) {
					debug('Failed to load status for queue %d: %o', queue.id, err);
					states.set(queue.id, { queue, status: null, loading: false });
				}
			})
		);

		setQueueStates(states);
		setLoading(false);
	}, [open, queues]);

	useEffect(() => {
		loadAllStatuses();
	}, [loadAllStatuses]);

	const enabledCount = Array.from(queueStates.values()).filter(
		(s) => s.status?.enabled
	).length;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-6xl max-h-[90vh] p-0 gap-0 overflow-hidden">
				<DialogHeader className="px-6 pt-6 pb-4 border-b">
					<div className="flex items-center gap-2">
						<Globe className="size-5 text-muted-foreground" />
						<DialogTitle className="text-xl">Public Access Manager</DialogTitle>
					</div>
					<DialogDescription>
						Manage public access for all queues. {enabledCount > 0 && (
							<span className="text-primary font-medium">
								{enabledCount} queue{enabledCount !== 1 ? 's' : ''} with public access enabled
							</span>
						)}
					</DialogDescription>
				</DialogHeader>

				<ScrollArea className="flex-1 max-h-[calc(90vh-10rem)]">
					<div className="px-6 py-4">
						{loading ? (
							<div className="flex items-center justify-center py-12">
								<Loader2 className="size-6 animate-spin text-muted-foreground" />
								<span className="ml-2 text-sm text-muted-foreground">
									Loading public access status...
								</span>
							</div>
						) : queueStates.size === 0 ? (
							<div className="text-center py-12 text-muted-foreground">
								<Globe className="size-12 mx-auto mb-4 opacity-20" />
								<p>No queues available</p>
							</div>
						) : (
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Queue</TableHead>
										<TableHead>Status</TableHead>
										<TableHead>Public URL</TableHead>
										<TableHead className="text-right">Actions</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{Array.from(queueStates.values()).map(({ queue, status, loading: itemLoading }) => (
										<TableRow key={queue.id}>
											{/* Queue name */}
											<TableCell>
												<div className="flex items-center gap-2">
													<div
														className="size-3 rounded-full shrink-0"
														style={{ backgroundColor: queue.color }}
													/>
													{queue.icon && <span className="shrink-0 text-sm">{queue.icon}</span>}
													<span className="font-medium">{queue.name}</span>
												</div>
											</TableCell>

											{/* Status badge */}
											<TableCell>
												{status?.enabled ? (
													<PublicAccessBadge
														canRead={status.canRead}
														canWrite={status.canWrite}
														canDelete={status.canDelete}
														useCount={status.useCount}
													/>
												) : (
													<span className="text-xs text-muted-foreground">Disabled</span>
												)}
											</TableCell>

											{/* Public URL */}
											<TableCell>
												{status?.enabled && status.url ? (
													<div className="flex items-center gap-2">
														<Input
															value={status.url}
															readOnly
															disabled
															className="font-mono text-xs flex-1 min-w-0"
														/>
														<CopyUrlButton url={status.url} />
														<Button
															size="icon"
															variant="ghost"
															className="size-7 shrink-0"
															onClick={() => window.open(status.url, '_blank')}
														>
															<ExternalLink className="size-3" />
														</Button>
													</div>
												) : (
													<span className="text-xs text-muted-foreground">-</span>
												)}
											</TableCell>

											{/* Actions */}
											<TableCell className="text-right">
												{onQueueSettings && (
													<Button
														size="sm"
														variant="outline"
														onClick={() => onQueueSettings(queue)}
														disabled={itemLoading}
													>
														<SettingsIcon className="size-3 mr-1" />
														Settings
													</Button>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						)}
					</div>
				</ScrollArea>
			</DialogContent>
		</Dialog>
	);
}
